import {
  emptyVehicleDetailParameter,
  parseVehicleDetailParameter,
  type VehicleDetailParameter,
} from '../../../api/formSampling';
import { LHU_GROUPED_NAME_OPTIONS, type LhuGroupedName } from '../../../api/strapiCrm';

export type SamplingAirGroup = 'air' | 'non_air';

export type FinishMeasurementKey =
  | 'water_temperature'
  | 'dhl'
  | 'water_debit'
  | 'air_temperature'
  | 'salinity'
  | 'water_clarity'
  | 'ph'
  | 'tds'
  | 'dissolved_oxygen'
  | 'weather_condition'
  | 'wind_direction_condition';

export type FinishMeasurementSpec = { key: FinishMeasurementKey; label: string; testId: string };

export const WEATHER_CONDITION_OPTIONS = ['clear', 'rainy', 'cloudy'] as const;
export type WeatherConditionOption = (typeof WEATHER_CONDITION_OPTIONS)[number];

export function isWeatherConditionOption(value: string): value is WeatherConditionOption {
  return (WEATHER_CONDITION_OPTIONS as readonly string[]).includes(value);
}

export const WIND_DIRECTION_OPTIONS = [
  'North - South',
  'South - North',
  'West - East',
  'East - West',
] as const;
export type WindDirectionOption = (typeof WIND_DIRECTION_OPTIONS)[number];

export function isWindDirectionOption(value: string): value is WindDirectionOption {
  return (WIND_DIRECTION_OPTIONS as readonly string[]).includes(value);
}

export const FINISH_FIELD_MEASUREMENT_AIR: FinishMeasurementSpec[] = [
  { key: 'water_temperature', label: 'Suhu air', testId: 'survey-berita-acara-finish-water-temperature' },
  { key: 'dhl', label: 'DHL', testId: 'survey-berita-acara-finish-dhl' },
  { key: 'water_debit', label: 'Debit Air', testId: 'survey-berita-acara-finish-water-debit' },
  { key: 'air_temperature', label: 'Suhu udara', testId: 'survey-berita-acara-finish-air-temperature' },
  { key: 'salinity', label: 'Salinitas', testId: 'survey-berita-acara-finish-salinity' },
  { key: 'water_clarity', label: 'Kecerahan', testId: 'survey-berita-acara-finish-water-clarity' },
  { key: 'ph', label: 'pH', testId: 'survey-berita-acara-finish-ph' },
  { key: 'tds', label: 'TDS', testId: 'survey-berita-acara-finish-tds' },
  { key: 'dissolved_oxygen', label: 'DO', testId: 'survey-berita-acara-finish-do' },
  { key: 'weather_condition', label: 'Cuaca', testId: 'survey-berita-acara-finish-weather' },
];

export const FINISH_FIELD_MEASUREMENT_NON_AIR: FinishMeasurementSpec[] = [
  { key: 'weather_condition', label: 'Cuaca', testId: 'survey-berita-acara-finish-weather' },
  { key: 'wind_direction_condition', label: 'Arah angin', testId: 'survey-berita-acara-finish-wind' },
];

export function finishFieldMeasurementSpecs(group: SamplingAirGroup): FinishMeasurementSpec[] {
  return group === 'air' ? FINISH_FIELD_MEASUREMENT_AIR : FINISH_FIELD_MEASUREMENT_NON_AIR;
}

export function parseSamplingAirGroup(value: unknown): SamplingAirGroup {
  return value === 'air' ? 'air' : 'non_air';
}

export type FinishDraft = {
  location_name: string;
  location_address: string;
  weather_condition: string;
  wind_direction_condition: string;
  water_temperature: string;
  dhl: string;
  water_debit: string;
  air_temperature: string;
  salinity: string;
  water_clarity: string;
  ph: string;
  tds: string;
  dissolved_oxygen: string;
  jumlah_sampel: string;
  is_having_blanko_wadah_penyerap: boolean;
  abnormalitas_note: string;
  keterangan_foto: string;
  location_sketsa_picture: string;
  lhu_grouped_name: LhuGroupedName | '';
  longitude: string;
  latitude: string;
  vehicle_information: VehicleDetailParameter;
};

const EMPTY_MEASUREMENTS: Pick<FinishDraft, FinishMeasurementKey> = {
  weather_condition: '',
  wind_direction_condition: '',
  water_temperature: '',
  dhl: '',
  water_debit: '',
  air_temperature: '',
  salinity: '',
  water_clarity: '',
  ph: '',
  tds: '',
  dissolved_oxygen: '',
};

export const emptyFinishDraft = (): FinishDraft => ({
  location_name: '',
  location_address: '',
  ...EMPTY_MEASUREMENTS,
  jumlah_sampel: '',
  is_having_blanko_wadah_penyerap: false,
  abnormalitas_note: '',
  keterangan_foto: '',
  location_sketsa_picture: '',
  lhu_grouped_name: '',
  longitude: '',
  latitude: '',
  vehicle_information: emptyVehicleDetailParameter(),
});

export function isFinishDraftLocationComplete(
  draft: FinishDraft,
  airGroup: SamplingAirGroup = 'non_air',
): boolean {
  if (!draft.location_name.trim() || !isWeatherConditionOption(draft.weather_condition.trim())) {
    return false;
  }
  if (airGroup === 'non_air' && !isWindDirectionOption(draft.wind_direction_condition.trim())) {
    return false;
  }
  return true;
}

/** Simpan dan selesai: foto diutamakan. Tanpa foto, keterangan foto wajib. */
export function finishPhotoOrKeteranganError(draft: FinishDraft): string | null {
  if (draft.location_sketsa_picture.trim()) return null;
  if (draft.keterangan_foto.trim()) return null;
  return 'Foto belum ada. Isi keterangan foto, atau unggah foto.';
}

function coordInput(value: unknown): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value).trim();
}

export function isLhuGroupedName(value: string): value is LhuGroupedName {
  return LHU_GROUPED_NAME_OPTIONS.some((opt) => opt.value === value);
}

export function finishDraftFromLetterDetail(
  detail: (Omit<Partial<FinishDraft>, 'longitude' | 'latitude' | 'lhu_grouped_name'> & {
    longitude?: unknown;
    latitude?: unknown;
    lhu_grouped_name?: string;
  }) | null | undefined,
): FinishDraft {
  const base = emptyFinishDraft();
  if (!detail) return base;
  return {
    location_name: String(detail.location_name ?? '').trim(),
    location_address: String(detail.location_address ?? '').trim(),
    weather_condition: String(detail.weather_condition ?? '').trim(),
    wind_direction_condition: String(detail.wind_direction_condition ?? '').trim(),
    water_temperature: String(detail.water_temperature ?? '').trim(),
    dhl: String(detail.dhl ?? '').trim(),
    water_debit: String(detail.water_debit ?? '').trim(),
    air_temperature: String(detail.air_temperature ?? '').trim(),
    salinity: String(detail.salinity ?? '').trim(),
    water_clarity: String(detail.water_clarity ?? '').trim(),
    ph: String(detail.ph ?? '').trim(),
    tds: String(detail.tds ?? '').trim(),
    dissolved_oxygen: String(detail.dissolved_oxygen ?? '').trim(),
    jumlah_sampel: String(detail.jumlah_sampel ?? '').trim(),
    is_having_blanko_wadah_penyerap: Boolean(detail.is_having_blanko_wadah_penyerap),
    abnormalitas_note: String(detail.abnormalitas_note ?? ''),
    keterangan_foto: String(detail.keterangan_foto ?? '').trim(),
    location_sketsa_picture: String(detail.location_sketsa_picture ?? '').trim(),
    lhu_grouped_name: isLhuGroupedName(String(detail.lhu_grouped_name ?? '').trim())
      ? (String(detail.lhu_grouped_name).trim() as LhuGroupedName)
      : '',
    longitude: coordInput(detail.longitude),
    latitude: coordInput(detail.latitude),
    vehicle_information: parseVehicleDetailParameter(detail.vehicle_information),
  };
}

export function finishVehicleInformationPayload(draft: FinishDraft): VehicleDetailParameter {
  return parseVehicleDetailParameter(draft.vehicle_information);
}

export function finishMeasurementPayload(draft: FinishDraft): Pick<FinishDraft, FinishMeasurementKey> {
  return {
    weather_condition: draft.weather_condition.trim(),
    wind_direction_condition: draft.wind_direction_condition.trim(),
    water_temperature: draft.water_temperature.trim(),
    dhl: draft.dhl.trim(),
    water_debit: draft.water_debit.trim(),
    air_temperature: draft.air_temperature.trim(),
    salinity: draft.salinity.trim(),
    water_clarity: draft.water_clarity.trim(),
    ph: draft.ph.trim(),
    tds: draft.tds.trim(),
    dissolved_oxygen: draft.dissolved_oxygen.trim(),
  };
}
